import { FileText } from 'lucide-react';
import { useEffect, useState, type ReactElement } from 'react';

import { OFFICE_HOSTS, type OfficeHostId } from './constants';
import { HostCanvas } from './HostCanvas';
import { OfficeHostLogo } from './OfficeLogos';

const CYCLE: OfficeHostId[] = ['word', 'powerpoint', 'excel'];
const CYCLE_MS = 2000;

function nextHost(current: OfficeHostId): OfficeHostId {
  const index = CYCLE.indexOf(current);
  const nextIndex = index === -1 ? 0 : (index + 1) % CYCLE.length;
  return CYCLE[nextIndex] ?? 'word';
}

function prefersReducedMotion(): boolean {
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

export function OfficeWindowPreview(): ReactElement {
  const [hostId, setHostId] = useState<OfficeHostId>('word');
  const host = OFFICE_HOSTS.find((item) => item.id === hostId) ?? OFFICE_HOSTS[0];

  useEffect(() => {
    if (prefersReducedMotion()) return;

    const timer = window.setInterval(() => {
      setHostId((current) => nextHost(current));
    }, CYCLE_MS);

    return () => {
      window.clearInterval(timer);
    };
  }, [hostId]);

  return (
    <div className="border-border bg-card overflow-hidden rounded-2xl border shadow-[0_24px_60px_-28px_rgba(38,37,30,0.45)]">
      <div className="border-border flex gap-1 border-b px-2 py-2">
        {OFFICE_HOSTS.map((item) => {
          const isActive = item.id === hostId;
          return (
            <button
              key={item.id}
              type="button"
              onClick={() => {
                setHostId(item.id);
              }}
              className={`relative inline-flex h-8 items-center gap-1.5 overflow-hidden rounded-md px-2.5 text-xs font-semibold transition-colors ${
                isActive
                  ? 'bg-muted text-foreground'
                  : 'text-muted-foreground hover:text-foreground hover:bg-muted/60'
              }`}
            >
              <OfficeHostLogo hostId={item.id} className="size-3.5 shrink-0" />
              {item.name}
              {isActive ? (
                <span
                  key={hostId}
                  className="host-tab-progress bg-primary/80 absolute inset-x-0 bottom-0 h-0.5 origin-left"
                />
              ) : null}
            </button>
          );
        })}
      </div>

      <div className="border-border bg-muted/80 flex items-center gap-2 border-b px-3 py-2">
        <span className="size-2 rounded-full bg-[#cf2d56]/70" />
        <span className="size-2 rounded-full bg-[#c08532]/70" />
        <span className="size-2 rounded-full bg-[#1f8a65]/70" />
        <p className="text-muted-foreground ml-2 font-mono text-[10px] tracking-wide uppercase">
          {host.chromeTitle}
        </p>
      </div>

      <div className="grid min-h-[320px] grid-cols-1 md:grid-cols-[minmax(0,1fr)_220px]">
        <div className="bg-muted flex items-center justify-center px-6 py-5 md:border-r md:border-[var(--border)]">
          <div key={host.id} className="host-canvas-fade w-full">
            <HostCanvas hostId={host.id} />
          </div>
        </div>

        <aside className="bg-background flex flex-col">
          <div className="border-border flex items-center gap-2 border-b px-3 py-2.5">
            <img src="/assets/icon-32.png" alt="" className="size-5" />
            <div>
              <p className="text-foreground text-xs leading-none font-semibold">DocuID</p>
              <p className="text-muted-foreground mt-0.5 font-mono text-[9px] tracking-wide uppercase">
                {host.name} pane
              </p>
            </div>
          </div>
          <div key={host.id} className="host-canvas-fade flex flex-1 flex-col gap-2 p-3">
            <p className="text-foreground text-[11px] font-semibold">{host.paneLabel}</p>
            {host.files.map((file, index) => (
              <div
                key={file.name}
                className={`flex items-center gap-2 rounded-lg border px-2.5 py-2 ${
                  index === 0 ? 'border-primary/40 bg-primary/5' : 'border-border bg-card'
                }`}
              >
                <FileText className="text-primary size-3.5 shrink-0" />
                <div className="min-w-0">
                  <p className="text-foreground truncate text-[11px] font-medium">{file.name}</p>
                  <p className="text-muted-foreground font-mono text-[9px]">{file.meta}</p>
                </div>
              </div>
            ))}
          </div>
        </aside>
      </div>
    </div>
  );
}
